import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { EMPTY_DATA, downloadDataFile } from './dataFile';

const SS_DATA = 'pinaki:data';
const SS_AUTH = 'pinaki:authed';

const Ctx = createContext(null);

export function useStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore outside provider');
  return ctx;
}

function loadSessionData() {
  try {
    const raw = sessionStorage.getItem(SS_DATA);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

export function StoreProvider({ children }) {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SS_AUTH) === '1');
  const [data, setDataState] = useState(() => loadSessionData());
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);

  // Mirror to sessionStorage on every change so refresh doesn't wipe in-progress work.
  // sessionStorage is per-tab and dies when the tab closes.
  useEffect(() => {
    if (data) sessionStorage.setItem(SS_DATA, JSON.stringify(data));
  }, [data]);

  // beforeunload guard for unsaved changes
  useEffect(() => {
    function onBeforeUnload(e) {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const setAuth = useCallback((v) => {
    setAuthed(v);
    if (v) sessionStorage.setItem(SS_AUTH, '1');
    else sessionStorage.removeItem(SS_AUTH);
  }, []);

  const startSession = useCallback((newData) => {
    setDataState(newData);
    setDirty(false);
  }, []);

  const updateData = useCallback((updater) => {
    setDataState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
    setDirty(true);
  }, []);

  const saveFile = useCallback(() => {
    if (!data) return;
    downloadDataFile(data);
    setDirty(false);
  }, [data]);

  // Atomically apply a new data object AND save it to disk.
  // Used by Generate to keep counter/history persisted without relying on the user
  // remembering to click Save.
  const commitAndSave = useCallback((nextData) => {
    setDataState(nextData);
    downloadDataFile(nextData);
    setDirty(false);
  }, []);

  const lock = useCallback(() => {
    sessionStorage.removeItem(SS_DATA);
    sessionStorage.removeItem(SS_AUTH);
    setDataState(null);
    setAuthed(false);
    setDirty(false);
  }, []);

  return (
    <Ctx.Provider value={{
      authed, setAuth,
      data, updateData, startSession,
      dirty, saveFile, commitAndSave, lock,
      EMPTY_DATA,
    }}>
      {children}
    </Ctx.Provider>
  );
}
